
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DeleteMealSlotOptions, MealType, FoodItem, MealSlot } from '@/types/meal-plan';
import { ClientIntakeData } from '@/components/meal-planning/ClientIntakeForm';
import MealSwapModal from './MealSwapModal';
import MealSlotCard from './MealSlotCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Plus, Check, X, Loader2, ChefHat } from 'lucide-react';

interface MealPlanGridProps {
  mealSlots: MealSlot[];
  onAddFood: (food: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onRemoveFood: (foodIndex: number, day: number, mealType: MealType, stateKey?: string) => void;
  selectedDay: number;
  clientPreferences?: Partial<ClientIntakeData>;
  onSwapFood?: (oldFood: FoodItem, newFood: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onUpdateMealInfo?: (day: number, mealType: MealType, updates: { name?: string; timeSlot?: string; targetKcal?: number | null }, stateKey?: string) => void;
  onDeleteMealSlot?: (day: number, mealName: string, options?: DeleteMealSlotOptions) => Promise<void>;
  onDraftDataUpdate?: (draftData: any) => void;
  clientId?: string;
  cuisineTypes?: string[];
  draftId?: string;
  onAddMealType?: (day: number, mealName: string, mealTime: string, targetCalories?: number | null) => Promise<void>;
  allergies?: string[];
  dietaryPreferences?: string[];
  isDeletingAnyMealSlot?: boolean;
}

const MealPlanGrid: React.FC<MealPlanGridProps> = ({ 
  mealSlots, 
  onAddFood, 
  onRemoveFood,
  selectedDay,
  clientPreferences,
  onSwapFood,
  onUpdateMealInfo,
  onDeleteMealSlot,
  onDraftDataUpdate,
  clientId,
  cuisineTypes = [],
  draftId,
  onAddMealType,
  allergies = [],
  dietaryPreferences = [],
  isDeletingAnyMealSlot = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [selectedMealSlot, setSelectedMealSlot] = useState<MealSlot | null>(null);
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [isAddingMealType, setIsAddingMealType] = useState(false);
  const [isSavingMealType, setIsSavingMealType] = useState(false);
  const [newMealName, setNewMealName] = useState('');
  const [newMealTime, setNewMealTime] = useState('');
  const [newTargetCalories, setNewTargetCalories] = useState('');

  // Sort meal slots by type to ensure consistent order
  const sortedSlots = [...mealSlots].sort((a, b) => {
    const order: Record<MealType, number> = {
      breakfast: 1,
      snack: 2,
      lunch: 3,
      dinner: 4,
      "pre-breakfast": 0,
      "mid-morning-snack": 1.5,
      "afternoon-snack": 3.5,
      morningSnack: 2,
      afternoonSnack: 4
    };
    return order[a.type] - order[b.type];
  });

  const handleSwapClick = (mealSlot: MealSlot, food: FoodItem) => {
    setSelectedMealSlot(mealSlot);
    setSelectedFood(food);
    setSwapModalOpen(true);
  };

  const handleSwapMeal = (newFood: FoodItem) => {
    if (selectedMealSlot && selectedFood && onSwapFood) {
      onSwapFood(selectedFood, newFood, selectedDay, selectedMealSlot.type);
    }
  };

  const handleAddMealType = async () => {
    if (!newMealName || !newMealTime || !onAddMealType) return;
    
    setIsSavingMealType(true);
    try {
      const targetCal = newTargetCalories ? parseInt(newTargetCalories) : null;
      await onAddMealType(selectedDay, newMealName, newMealTime, targetCal);
      setIsAddingMealType(false);
      setNewMealName('');
      setNewMealTime('');
      setNewTargetCalories('');
    } catch (error) {
      console.error('Error adding meal type:', error);
    } finally {
      setIsSavingMealType(false);
    }
  };

  const handleCancelAddMealType = () => {
    setIsAddingMealType(false);
    setNewMealName('');
    setNewMealTime('');
    setNewTargetCalories('');
  };

  return (
    <div className="space-y-6">
      {/* Create Custom Recipe Button - At the top */}
      {clientId && draftId && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
          onClick={() => {
            // Navigate to custom recipe creation page with context
            const params = new URLSearchParams({
              clientId: clientId,
              draftId: draftId,
              day: selectedDay.toString(),
              from: `${location.pathname}${location.search}`,
            });
            navigate(`/recipes/create?${params.toString()}`);
          }}
        >
          <ChefHat className="h-4 w-4 mr-2" />
          Create Custom Recipe
        </Button>
      )}

      {sortedSlots.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Meal Types Available</h3>
          <p className="text-sm text-gray-500 max-w-md">
            Please select a client with configured meal programmes, or set up meal programmes for the selected client first.
          </p>
        </div>
      ) : (
        <>
          {sortedSlots.map((slot, index) => (
            <MealSlotCard
              key={slot.id}
              slot={slot}
              mealOrder={index + 1}
              selectedDay={selectedDay}
              onAddFood={onAddFood}
              onRemoveFood={onRemoveFood}
              onSwapFood={onSwapFood}
              onUpdateMealInfo={onUpdateMealInfo}
              onDeleteMealSlot={onDeleteMealSlot}
              onDraftDataUpdate={onDraftDataUpdate}
              clientId={clientId}
              cuisineTypes={cuisineTypes}
              draftId={draftId}
              allergies={allergies}
              dietaryPreferences={dietaryPreferences}
              isDeletingAnyMealSlot={isDeletingAnyMealSlot}
            />
          ))}
          
          {/* Inline Add New Meal Type Form */}
          {onAddMealType && isAddingMealType && (
            <Card className="border-2 border-blue-500">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Add New Meal Type</h3>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddMealType}
                      disabled={!newMealName || !newMealTime || isSavingMealType}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isSavingMealType ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Save
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelAddMealType}
                      disabled={isSavingMealType}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="new-meal-name" className="text-xs font-medium text-muted-foreground">
                      Meal Name
                    </Label>
                    <Input
                      id="new-meal-name"
                      value={newMealName}
                      onChange={(e) => setNewMealName(e.target.value)}
                      placeholder="e.g., Pre-Workout"
                      className="mt-1"
                      disabled={isSavingMealType}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-meal-time" className="text-xs font-medium text-muted-foreground">
                      Meal Time
                    </Label>
                    <Input
                      id="new-meal-time"
                      type="time"
                      value={newMealTime}
                      onChange={(e) => setNewMealTime(e.target.value)}
                      className="mt-1"
                      disabled={isSavingMealType}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-target-calories" className="text-xs font-medium text-muted-foreground">
                      Target Calories (Optional)
                    </Label>
                    <Input
                      id="new-target-calories"
                      type="number"
                      value={newTargetCalories}
                      onChange={(e) => setNewTargetCalories(e.target.value)}
                      placeholder="e.g., 300 (optional)"
                      className="mt-1"
                      disabled={isSavingMealType}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Add New Meal Type Button */}
          {onAddMealType && !isAddingMealType && (
            <Button
              variant="outline"
              className="w-full border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 py-8"
              onClick={() => setIsAddingMealType(true)}
            >
              <Plus className="h-5 w-5 mr-2" />
              Add New Meal Type
            </Button>
          )}
        </>
      )}

      {selectedMealSlot && selectedFood && (
        <MealSwapModal
          open={swapModalOpen}
          onOpenChange={setSwapModalOpen}
          mealSlot={selectedMealSlot}
          originalFood={selectedFood}
          onSwapMeal={handleSwapMeal}
          clientPreferences={clientPreferences}
        />
      )}
    </div>
  );
};

export default MealPlanGrid;
